import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  LockOutlined as LockIcon,
} from "@mui/icons-material";
import { useSubscription } from "../hooks/useSubscription";
import { FREE_PROJECT_LIMIT } from "../pages/contractor/useProjects";

const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID ?? "";

const PRO_FEATURES = [
  "Unlimited active projects",
  "Unlimited clients",
  "Priority support",
  "Advanced milestone tracking",
  "Document storage",
];

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
}

export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const { startCheckout } = useSubscription();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    await startCheckout(PRICE_ID);
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={3} alignItems="center">
          <Box
            sx={{
              bgcolor: "primary.main",
              borderRadius: "50%",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockIcon sx={{ color: "white", fontSize: 32 }} />
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              You've reached the free limit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The free plan includes{" "}
              <strong>{FREE_PROJECT_LIMIT} active projects</strong>. Upgrade to
              Pro for unlimited projects and clients.
            </Typography>
          </Box>

          <Divider sx={{ width: "100%" }} />

          <Stack spacing={1.5} sx={{ width: "100%" }}>
            {PRO_FEATURES.map((feature) => (
              <Box key={feature} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckIcon color="success" fontSize="small" />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            ))}
          </Stack>

          <Stack spacing={1} sx={{ width: "100%" }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleUpgrade}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : undefined}
            >
              {loading ? "Redirecting…" : "Upgrade to Pro — $49.99/mo"}
            </Button>
            <Button variant="text" fullWidth onClick={onClose}>
              Maybe later
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
